import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import Cropper, { type Area } from "react-easy-crop";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface AvatarCropDialogProps {
  /** The picked image file; the dialog is open while this is non-null. */
  file: File | null;
  onCancel: () => void;
  /** Called with the cropped square image, ready to upload. */
  onSave: (cropped: File) => void;
}

// Cap the stored avatar at 512px — plenty for every surface (largest render
// is the xl 80px avatar) and keeps uploads small.
const OUTPUT_SIZE = 512;

async function cropToFile(src: string, area: Area, original: File): Promise<File | null> {
  const img = new Image();
  img.src = src;
  await img.decode();

  const size = Math.min(OUTPUT_SIZE, Math.max(1, Math.round(area.width)));
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, size, size);

  // Keep PNG for PNG sources (transparency); everything else re-encodes as JPEG.
  const type = original.type === "image/png" ? "image/png" : "image/jpeg";
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, 0.92));
  if (!blob) return null;
  const base = original.name.replace(/\.[^.]+$/, "") || "avatar";
  const ext = type === "image/png" ? "png" : "jpg";
  return new File([blob], `${base}.${ext}`, { type });
}

/**
 * Pan/zoom crop step between picking a profile picture and uploading it. The
 * image is dragged/zoomed inside a fixed circular frame; Save produces a
 * square crop of what's in the frame.
 */
export function AvatarCropDialog({ file, onCancel, onSave }: AvatarCropDialogProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedArea, setCroppedArea] = useState<Area | null>(null);
  const [saving, setSaving] = useState(false);

  const imageUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => {
    return () => {
      if (imageUrl) URL.revokeObjectURL(imageUrl);
    };
  }, [imageUrl]);

  // Reset the crop state whenever a new file comes in — the dialog component
  // stays mounted between opens.
  useEffect(() => {
    if (!file) return;
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedArea(null);
    setSaving(false);
  }, [file]);

  async function handleSave() {
    if (!imageUrl || !file || !croppedArea) return;
    setSaving(true);
    try {
      const cropped = await cropToFile(imageUrl, croppedArea, file);
      if (!cropped) {
        toast.error("Could not crop the image");
        return;
      }
      onSave(cropped);
    } catch {
      toast.error("Could not crop the image");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={file !== null} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Adjust photo</DialogTitle>
          <DialogDescription>
            Drag to reposition and zoom to fit the photo in the frame.
          </DialogDescription>
        </DialogHeader>

        <div className="relative h-72 w-full overflow-hidden rounded-md bg-muted">
          {imageUrl && (
            <Cropper
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(_, areaPixels) => setCroppedArea(areaPixels)}
            />
          )}
        </div>

        <label className="flex items-center gap-3 text-sm text-muted-foreground">
          Zoom
          <input
            type="range"
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1 accent-primary"
            aria-label="Zoom"
          />
        </label>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void handleSave()} disabled={saving || !croppedArea}>
            {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
            Save photo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
